import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { Plus, Edit, Trash2, Play, Pause, Clock, Search, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getQueue, createQueueItem, updateQueueItem, deleteQueueItem } from '../services/queue';

const Queue = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    bot_type: 'react', // Add bot_type field
    channel_id: '',
    message_id: '',
    message_content: '',
    emoji: '',
    reactor_type: 'manual',
    priority: 10,
    scheduled_at: '',
    status: 'pending'
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch queue items from API
  const fetchQueue = async () => {
    try {
      setLoading(true);
      const response = await getQueue();
      setQueueItems(response);
    } catch (error) {
      console.error('Failed to fetch queue items:', error);
      showToast('Failed to load queue: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getReactorTypeVariant = (type) => {
    switch (type) {
      case 'manual': return 'primary';
      case 'scheduled': return 'info';
      case 'keyword': return 'warning';
      case 'user': return 'success';
      default: return 'secondary';
    }
  };

  const getBotTypeVariant = (type) => {
    switch (type) {
      case 'react': return 'primary';
      case 'message': return 'info';
      default: return 'secondary';
    }
  };

  const filteredItems = queueItems.filter(item => {
    const matchesSearch = item.channel_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.message_id && item.message_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.emoji && item.emoji.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.message_content && item.message_content.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const toggleRowExpansion = (itemId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(itemId)) {
      newExpandedRows.delete(itemId);
    } else {
      newExpandedRows.add(itemId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      bot_type: item.bot_type,
      channel_id: item.channel_id,
      message_id: item.message_id || '',
      message_content: item.message_content || '',
      emoji: item.emoji || '',
      reactor_type: item.reactor_type,
      priority: item.priority,
      scheduled_at: item.scheduled_at ? new Date(item.scheduled_at).toISOString().slice(0, 16) : '',
      status: item.status
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      bot_type: 'react',
      channel_id: '',
      message_id: '',
      message_content: '',
      emoji: '',
      reactor_type: 'manual',
      priority: 10,
      scheduled_at: '',
      status: 'pending'
    });
    setShowModal(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      try {
        await deleteQueueItem(itemToDelete.id);
        fetchQueue();
        showToast('Queue item deleted successfully');
      } catch (error) {
        console.error('Failed to delete queue item:', error);
        showToast('Failed to delete queue item: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setItemToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Prepare data based on bot_type
      const submitData = {
        bot_type: formData.bot_type,
        channel_id: formData.channel_id,
        reactor_type: formData.reactor_type,
        priority: formData.priority,
        scheduled_at: formData.scheduled_at || new Date().toISOString(),
        status: formData.status
      };

      // Add fields based on bot_type
      if (formData.bot_type === 'react') {
        submitData.message_id = formData.message_id;
        submitData.emoji = formData.emoji;
        submitData.token_count = 1;
        submitData.emojis_per_message = 1;
      } else if (formData.bot_type === 'message') {
        submitData.message_content = formData.message_content;
        // For message type, you might want to set message_id to null or empty
        submitData.message_id = null; // Explicitly set to null
      }

      if (editingItem) {
        await updateQueueItem(editingItem.id, submitData);
        showToast('Queue item updated successfully');
      } else {
        await createQueueItem(submitData);
        showToast('Queue item created successfully');
      }
      setShowModal(false);
      fetchQueue();
    } catch (error) {
      console.error('Failed to save queue item:', error);
      showToast('Failed to save queue item: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await updateQueueItem(item.id, { status: newStatus });
      showToast(`Queue item ${newStatus} successfully`);
      fetchQueue();
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast('Failed to update status: ' + (error.message || error), 'danger');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading queue...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Reaction Queue</h1>
          <p className="text-muted mb-0">
            Manage queued reactions and messages
          </p>
        </div>
        <Button variant="primary" onClick={handleAdd}>
          <Plus size={16} className="me-2" />
          <span className="d-none d-md-inline">Add to Queue</span>
          <span className="d-md-none">Add</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Clock size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{queueItems.length}</h5>
              <p className="text-muted mb-0 small">Total Items</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-warning mb-2" style={{ fontSize: '1.2rem' }}>⏰</div>
              <h5 className="fw-bold mb-0">{queueItems.filter(i => i.status === 'pending').length}</h5>
              <p className="text-muted mb-0 small">Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-info mb-2" style={{ fontSize: '1.2rem' }}>🔄</div>
              <h5 className="fw-bold mb-0">{queueItems.filter(i => i.status === 'processing').length}</h5>
              <p className="text-muted mb-0 small">Processing</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-success mb-2" style={{ fontSize: '1.2rem' }}>✅</div>
              <h5 className="fw-bold mb-0">{queueItems.filter(i => i.status === 'completed').length}</h5>
              <p className="text-muted mb-0 small">Completed</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search queue items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <div className="text-muted small d-flex align-items-center h-100 justify-content-end justify-content-md-start">
                {filteredItems.length} items
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Queue Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Type</th>
                  <th className="border-0 py-3">Channel</th>
                  <th className="border-0 py-3">Details</th>
                  <th className="border-0 py-3">Priority</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Scheduled</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} style={{ cursor: 'pointer' }}>
                    <td className="px-4 py-3" onClick={() => toggleRowExpansion(item.id)}>
                      <Badge bg={getBotTypeVariant(item.bot_type)}>
                        {item.bot_type}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(item.id)}>
                      <div className="fw-medium small">{item.channel_id}</div>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(item.id)}>
                      {item.bot_type === 'react' ? (
                        <>
                          <div className="fw-medium small">Message: {item.message_id}</div>
                          <div className="text-muted small">Emoji: {item.emoji}</div>
                        </>
                      ) : (
                        <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }}>
                          {item.message_content}
                        </div>
                      )}
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(item.id)}>
                      <Badge bg="secondary">{item.priority}</Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(item.id)}>
                      <Badge bg={getStatusVariant(item.status)}>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(item.id)}>
                      <small className="text-muted">{formatDate(item.scheduled_at)}</small>
                    </td>
                    <td className="py-3">
                      <div className="d-flex gap-2">
                        {item.status === 'pending' && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleStatusChange(item, 'processing')}
                          >
                            <Play size={14} />
                          </Button>
                        )}
                        {item.status === 'processing' && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleStatusChange(item, 'pending')}
                          >
                            <Pause size={14} />
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(item)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {filteredItems.map((item) => {
              const isExpanded = expandedRows.has(item.id);
              
              return (
                <Card key={item.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(item.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <Badge bg={getBotTypeVariant(item.bot_type)} className="me-1">
                            {item.bot_type}
                          </Badge>
                          <Badge bg={getStatusVariant(item.status)} className="me-1">
                            {item.status}
                          </Badge>
                          <Badge bg="secondary">
                            Prio: {item.priority}
                          </Badge>
                        </div>
                        <div className="fw-medium small">{item.channel_id}</div>
                        {item.bot_type === 'react' ? (
                          <>
                            <div className="text-muted small">Message: {item.message_id}</div>
                            <div className="text-muted small">Emoji: {item.emoji}</div>
                          </>
                        ) : (
                          <div className="text-muted small text-truncate">
                            {item.message_content}
                          </div>
                        )}
                      </div>
                      <div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <Row className="g-3">
                          <Col xs={12}>
                            <div className="text-muted small">Scheduled</div>
                            <div className="small">{formatDate(item.scheduled_at)}</div>
                          </Col>
                          <Col xs={12}>
                            <div className="text-muted small">Reactor Type</div>
                            <Badge bg={getReactorTypeVariant(item.reactor_type)}>
                              {item.reactor_type}
                            </Badge>
                          </Col>
                          <Col xs={12}>
                            <div className="d-flex gap-2 justify-content-center mt-2">
                              {item.status === 'pending' && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(item, 'processing');
                                  }}
                                >
                                  <Play size={14} />
                                </Button>
                              )}
                              {item.status === 'processing' && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(item, 'pending');
                                  }}
                                >
                                  <Pause size={14} />
                                </Button>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(item);
                                }}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(item);
                                }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-5 text-muted">
              No queue items found. {searchTerm ? 'Try a different search term.' : 'Add your first item!'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingItem ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Queue Item
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add to Queue
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Bot Type</Form.Label>
                  <Form.Select 
                    value={formData.bot_type}
                    onChange={(e) => handleInputChange('bot_type', e.target.value)}
                    className="py-2"
                  >
                    <option value="react">React to Message</option>
                    <option value="message">Send Message</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Channel ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter channel ID"
                    value={formData.channel_id}
                    onChange={(e) => handleInputChange('channel_id', e.target.value)}
                    className="py-2"
                    required
                  />
                </Form.Group>
              </Col>
              
              {formData.bot_type === 'react' && (
                <>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Message ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter message ID"
                        value={formData.message_id}
                        onChange={(e) => handleInputChange('message_id', e.target.value)}
                        className="py-2"
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Emoji</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter emoji (e.g., 👍, 🎉)"
                        value={formData.emoji}
                        onChange={(e) => handleInputChange('emoji', e.target.value)}
                        className="py-2"
                        required
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              
              {formData.bot_type === 'message' && (
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Message Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter message content"
                      value={formData.message_content}
                      onChange={(e) => handleInputChange('message_content', e.target.value)}
                      className="py-2"
                      required
                    />
                  </Form.Group>
                </Col>
              )}
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Reactor Type</Form.Label>
                  <Form.Select 
                    value={formData.reactor_type}
                    onChange={(e) => handleInputChange('reactor_type', e.target.value)}
                    className="py-2"
                  >
                    <option value="manual">Manual</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="keyword">Keyword</option>
                    <option value="user">User</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Priority</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 10)}
                    className="py-2"
                  />
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select 
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="py-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Scheduled At</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => handleInputChange('scheduled_at', e.target.value)}
                    className="py-2"
                  />
                  <Form.Text className="text-muted">
                    Leave empty for immediate execution
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingItem ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingItem ? 'Update Item' : 'Add to Queue'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-danger">
            <AlertTriangle size={24} className="me-2" />
            Delete Queue Item
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <Trash2 size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold">Are you sure you want to delete this queue item?</h5>
            {itemToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="fw-medium">Type: {itemToDelete.bot_type}</div>
                <div className="fw-medium">Channel: {itemToDelete.channel_id}</div>
                {itemToDelete.bot_type === 'react' ? (
                  <>
                    <div className="text-muted small">Message: {itemToDelete.message_id}</div>
                    <div className="text-muted small">Emoji: {itemToDelete.emoji}</div>
                  </>
                ) : (
                  <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }}>
                    Content: {itemToDelete.message_content}
                  </div>
                )}
                <div className="mt-2">
                  <Badge bg={getStatusVariant(itemToDelete.status)} className="me-1">
                    {itemToDelete.status}
                  </Badge>
                  <Badge bg={getReactorTypeVariant(itemToDelete.reactor_type)} className="me-1">
                    {itemToDelete.reactor_type}
                  </Badge>
                  <Badge bg="secondary" className="me-1">
                    Prio: {itemToDelete.priority}
                  </Badge>
                </div>
              </div>
            )}
            <p className="text-muted mt-3">
              This action cannot be undone. The queue item will be permanently removed.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={handleDeleteCancel}
            className="px-4"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            className="px-4"
          >
            <Trash2 size={16} className="me-2" />
            Delete Item
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Queue;